import React, { useState, useMemo } from "react";
import {
	IonModal,
	IonContent,
	IonList,
	IonItem,
	IonLabel,
	IonIcon,
	IonSkeletonText,
	IonHeader,
	IonToolbar,
	IonTitle,
	IonButtons,
	IonButton,
} from "@ionic/react";
import { lockClosed, peopleOutline, eyeOutline, closeOutline } from "ionicons/icons";
import { useNoteVisibility, AccessSource, Viewer } from "../../hooks/useNoteVisibility";
import ContactProfileImage from "../common/ContactProfileImage";
import "./NoteVisibilityIndicator.css";

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Priority order for choosing the most specific access source to display. */
const SOURCE_PRIORITY: AccessSource["type"][] = [
	"ptp_role",
	"ptp_contact",
	"document_acl",
	"ptp_all_staff",
	"author",
];

/** Map an `AccessSource` to a short human-readable string. */
const formatAccessSource = (source: AccessSource): string => {
	switch (source.type) {
		case "author":
			return "Note author";
		case "document_acl":
			return `Via document permissions${source.role ? ` (${source.role})` : ""}`;
		case "ptp_contact":
			return `Via ${source.label || "contact"} category`;
		case "ptp_role":
			return `Via ${source.role_label || source.label || "role"} role`;
		case "ptp_all_staff":
			return `All staff access${source.label ? ` (${source.label})` : ""}`;
		default:
			return "Has access";
	}
};

/** Pick the most specific access reason for a viewer. */
const getBestAccessReason = (sources: AccessSource[]): string => {
	if (!sources || sources.length === 0) return "Has access";
	const unknownPriority = SOURCE_PRIORITY.length;
	const sorted = [...sources].sort((a, b) => {
		const ai = SOURCE_PRIORITY.indexOf(a.type);
		const bi = SOURCE_PRIORITY.indexOf(b.type);
		return (ai === -1 ? unknownPriority : ai) - (bi === -1 ? unknownPriority : bi);
	});
	return formatAccessSource(sorted[0]);
};

// ── Constants ──────────────────────────────────────────────────────────────────

/** Maximum number of overlapping avatars in the compact strip. */
const MAX_AVATARS = 5;

// ── Component ──────────────────────────────────────────────────────────────────

interface NoteVisibilityIndicatorProps {
	/** The parent note's `_id`. */
	noteId: string;
	/** Whether the confidential toggle is currently active. */
	confidential: boolean;
	/** Live category from the note form -- triggers re-fetch when the user changes category. */
	category?: string | null;
}

/**
 * NoteVisibilityIndicator
 *
 * Compact strip showing who can see a note; clicking opens a modal with the
 * full viewer list. Ported from my-pymble-portal.
 */
const NoteVisibilityIndicator: React.FC<NoteVisibilityIndicatorProps> = ({
	noteId,
	confidential,
	category,
}) => {
	const [modalOpen, setModalOpen] = useState(false);

	const { data, isLoading } = useNoteVisibility({ noteId, confidential, category });

	const displayedViewers = useMemo(
		() => (data?.viewers ?? []).slice(0, MAX_AVATARS),
		[data?.viewers]
	);

	const remainingCount = (data?.viewer_count ?? 0) - displayedViewers.length;

	if (isLoading) {
		return (
			<div className="visibility-strip visibility-strip--loading">
				<div className="visibility-avatars">
					{[0, 1, 2].map((i) => (
						<IonSkeletonText key={i} animated className="visibility-avatar-skeleton" />
					))}
				</div>
				<IonSkeletonText animated className="visibility-label-skeleton" />
			</div>
		);
	}

	if (!data) return null;

	const labelText = data.includes_all_staff
		? data.viewer_count > 0
			? `All staff (${data.viewer_count}) can view this note`
			: "All staff can view this note"
		: `${data.viewer_count} ${data.viewer_count === 1 ? "person" : "people"} can view`;

	const isCpOnly = data.visibility_mode === "cp_only";
	const hasViewerList = data.viewers.length > 0;

	return (
		<>
			{/* Compact strip */}
			<div
				className={`visibility-strip${isCpOnly ? " visibility-strip--cp" : ""}${!hasViewerList ? " visibility-strip--no-expand" : ""}`}
				onClick={hasViewerList ? () => setModalOpen(true) : undefined}
				role={hasViewerList ? "button" : undefined}
				tabIndex={hasViewerList ? 0 : undefined}
				onKeyDown={
					hasViewerList
						? (e) => {
								if (e.key === "Enter" || e.key === " ") {
									e.preventDefault();
									setModalOpen(true);
								}
						  }
						: undefined
				}
				aria-label={labelText}
			>
				{hasViewerList && (
					<div className="visibility-avatars">
						{displayedViewers.map((viewer) => (
							<div key={viewer._id} className="visibility-avatar-wrapper">
								<ContactProfileImage
									profile={{ contactId: viewer.contact_personal_id, name: viewer.name }}
									cssClass="visibility-avatar"
								/>
							</div>
						))}
						{remainingCount > 0 && (
							<div className="visibility-avatar-wrapper visibility-avatar-overflow">
								+{remainingCount}
							</div>
						)}
					</div>
				)}

				<span className="visibility-label">
					<IonIcon icon={data.includes_all_staff ? peopleOutline : eyeOutline} className="visibility-label-icon" />
					{labelText}
				</span>

				{isCpOnly && (
					<span className="visibility-cp-badge">
						<IonIcon icon={lockClosed} />
						CP only
					</span>
				)}
			</div>

			{/* Viewer list modal */}
			<IonModal isOpen={modalOpen} onDidDismiss={() => setModalOpen(false)} className="visibility-modal">
				<IonHeader>
					<IonToolbar>
						<IonIcon icon={peopleOutline} slot="start" className="visibility-modal-header-icon" />
						<IonTitle className="visibility-modal-title">
							Who can see this note ({data.viewer_count})
						</IonTitle>
						<IonButtons slot="end">
							<IonButton onClick={() => setModalOpen(false)}>
								<IonIcon slot="icon-only" icon={closeOutline} />
							</IonButton>
						</IonButtons>
					</IonToolbar>
				</IonHeader>
				<IonContent className="visibility-modal-content">
					{data.includes_all_staff && (
						<div className="visibility-banner visibility-banner--info">
							All current staff have access via category permissions
						</div>
					)}
					{isCpOnly && (
						<div className="visibility-banner visibility-banner--warning">
							<IonIcon icon={lockClosed} />
							Confidential mode — only CP authorised staff can see this comment
						</div>
					)}

					<IonList className="visibility-viewer-list" lines="none">
						{data.viewers.map((viewer: Viewer) => (
							<IonItem key={viewer._id} className="visibility-viewer-item">
								<div className="visibility-viewer-avatar" slot="start">
									<ContactProfileImage
										profile={{ contactId: viewer.contact_personal_id, name: viewer.name }}
										cssClass="visibility-viewer-avatar-img"
									/>
								</div>
								<IonLabel>
									<h3 className="visibility-viewer-name">{viewer.name}</h3>
									<p className="visibility-viewer-reason">
										{getBestAccessReason(viewer.access_sources)}
									</p>
								</IonLabel>
							</IonItem>
						))}
					</IonList>
				</IonContent>
			</IonModal>
		</>
	);
};

export default React.memo(NoteVisibilityIndicator);
